import React, { createContext, useState, useEffect, useContext } from 'react';
import api from '../services/api';

// Create the auth context
const AuthContext = createContext();

// Custom hook to use the auth context
export const useAuth = () => {
  return useContext(AuthContext);
};

// Authentication provider component
export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Initialize auth state on component mount
  useEffect(() => {
    // Check for token in local storage
    const token = localStorage.getItem('authToken');
    const userData = localStorage.getItem('userData');
    
    if (token && userData) {
      setCurrentUser(JSON.parse(userData));
      // Set default api auth header
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      // Verify token validity with backend
      verifyToken();
    } else {
      setLoading(false);
    }
  }, []);
  
  // Verify token with backend
  const verifyToken = async () => {
    try {
      const response = await api.get('/auth/check');
      if (response.data.status === 'success') {
        // Token is valid, keep user logged in
        setCurrentUser(response.data.admin);
      } else {
        // Token is invalid, log user out
        logout();
      }
    } catch (error) {
      // Handle expired or invalid token
      logout();
    } finally {
      setLoading(false);
    }
  };
  
  // Login function
  const login = async (username, password) => {
    try {
      setError('');
      const response = await api.post('/auth/login', {
        username,
        password
      });
      
      if (response.data.status === 'success') {
        const { token, admin } = response.data;
        
        // Store auth data
        localStorage.setItem('authToken', token);
        localStorage.setItem('userData', JSON.stringify(admin));
        
        // Set user in state
        setCurrentUser(admin);
        
        // Set default api auth header
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        return true;
      } else {
        setError(response.data.message || 'Login failed');
        return false;
      }
    } catch (error) {
      setError(
        error.response?.data?.message ||
        'Authentication failed. Please check your credentials.'
      );
      return false;
    }
  };
  
  // Initial admin setup function
  const setupAdmin = async (username, password) => {
    try {
      setError('');
      const response = await api.post('/auth/setup', {
        username,
        password
      });
      
      if (response.data.status === 'success') {
        return { success: true, message: response.data.message };
      } else {
        setError(response.data.message || 'Setup failed');
        return { success: false, message: response.data.message };
      }
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Admin setup failed';
      setError(errorMsg);
      return { success: false, message: errorMsg };
    }
  };
  
  // Logout function
  const logout = () => {
    // Clear local storage
    localStorage.removeItem('authToken');
    localStorage.removeItem('userData');
    
    // Clear current user
    setCurrentUser(null);
    
    // Remove auth header
    delete api.defaults.headers.common['Authorization'];
  };
  
  // Auth context value
  const value = {
    currentUser,
    login,
    logout,
    setupAdmin,
    error,
    setError,
    isAuthenticated: !!currentUser
  };
  
  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
